import { useState } from 'react'
import { Award, DollarSign, Layers } from 'lucide-react'
import { Input } from '@/components/ui/Input'
import { NumericInput } from '@/components/ui/NumericInput'
import { cn } from '@/utils/cn'
import { countIncludedServices } from './mergePlanServicesWithTemplate'
import type { PlanEditorCategoryGroup } from './planEditorConstants'
import { PlanEditorCalculatedField } from './PlanEditorCalculatedField'
import {
  SUPPORT_PRICING_OPTIONS,
  type SupportPricingMode,
} from '@/components/Configuracao/plansTabUtils'

const brl = (value: number) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value)

type PlanEditorDetailsTabProps = {
  isReadonly: boolean
  descricao: string
  onDescricaoChange: (value: string) => void
  valorParcela: number
  onValorParcelaChange: (value: number) => void
  valorEntrada: number
  onValorEntradaChange: (value: number) => void
  duration: number
  onDurationChange: (value: number) => void
  valorTotal: number
  showBonus: boolean
  onShowBonusChange: (value: boolean) => void
  bonusText: string
  onBonusTextChange: (value: string) => void
  servicesList: PlanEditorCategoryGroup[]
  supportPricingMode: SupportPricingMode
  onSupportPricingModeChange: (value: SupportPricingMode) => void
  guided?: boolean
}

export function PlanEditorDetailsTab({
  isReadonly,
  descricao,
  onDescricaoChange,
  valorParcela,
  onValorParcelaChange,
  valorEntrada,
  onValorEntradaChange,
  duration,
  onDurationChange,
  valorTotal,
  showBonus,
  onShowBonusChange,
  bonusText,
  onBonusTextChange,
  servicesList,
  supportPricingMode,
  onSupportPricingModeChange,
  guided = false,
}: PlanEditorDetailsTabProps) {
  const includedCount = countIncludedServices(servicesList)
  const [setupChoice, setSetupChoice] = useState<'unset' | 'no' | 'yes'>(
    valorEntrada > 0 ? 'yes' : 'unset',
  )

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="md:col-span-2">
          <Input
            label="Nome do Plano"
            value={descricao}
            onChange={(e) => onDescricaoChange(e.target.value)}
            disabled={isReadonly}
            placeholder="Ex: Plano START, Plano ADVANCED, Premium..."
          />
        </div>
        <div>
          <div className="flex flex-col gap-1.5">
            <label
              className="font-medium text-[var(--font-color-label)] uppercase tracking-wider"
              style={{ fontSize: 'var(--font-size-label)' }}
            >
              Tipo
            </label>
            <div className="w-full rounded-md border border-[var(--glass-border)] bg-[var(--glass-bg)] px-3 py-2 text-[13px] text-[var(--text-primary)]">
              Plano mensal recorrente
            </div>
          </div>
        </div>
      </div>

      <div className="glass-card p-4 space-y-4">
        <div className="flex items-center justify-between border-b border-white/[0.06] pb-2">
          <h4 className="text-xs font-bold text-accent uppercase tracking-wider flex items-center gap-1.5">
            <DollarSign size={14} />
            Precificação & Modelo Comercial
          </h4>
          <span className="rounded-md bg-accent px-3 py-1.5 text-xs font-semibold text-white">
            Plano mensal
          </span>
        </div>

        <div className="space-y-3">
            {guided ? (
              <div className="space-y-3">
                <ol className="space-y-1 text-xs text-text-secondary">
                  <li>1. Modelo comercial: Fixo mensal, já definido.</li>
                  <li>2. Informe o valor mensal no campo amarelo.</li>
                  <li>3. Diga se haverá custo de implantação. Se não, fica 0.</li>
                  <li>4. Fidelidade: 12 meses, já definida.</li>
                </ol>
                <div className="rounded-lg border border-white/[0.06] bg-white/[0.03] px-3 py-2 text-xs">
                  <span className="block font-semibold text-text-primary">Fixo mensal</span>
                  <span className="block text-text-muted">Valor mensal fechado, sem percentual sobre o faturamento.</span>
                </div>
              </div>
            ) : (
            <div className="relative z-10 grid grid-cols-1 md:grid-cols-2 gap-2">
              {SUPPORT_PRICING_OPTIONS.map((option) => {
                const active = supportPricingMode === option.id
                return (
                  <button
                    key={option.id}
                    type="button"
                    aria-pressed={active}
                    onClick={(event) => {
                      event.preventDefault()
                      event.stopPropagation()
                      onSupportPricingModeChange(option.id)
                      onValorParcelaChange(option.baseValue)
                    }}
                    disabled={isReadonly}
                    className={`rounded-lg border px-3 py-2 text-left text-xs transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${
                      active
                        ? 'border-accent bg-accent text-white'
                        : 'border-white/[0.06] bg-white/[0.03] text-text-muted hover:text-text-primary'
                    }`}
                  >
                    <span className="block font-semibold">{option.label}</span>
                    <span className="block text-[11px]">{option.description}</span>
                  </button>
                )
              })}
            </div>
            )}
            <div className={cn('grid grid-cols-1 gap-3 items-start', guided ? 'md:grid-cols-3' : 'md:grid-cols-4')}>
            <div className={guided ? 'rounded-lg border-2 border-warning p-2' : undefined}>
            <NumericInput
              label={supportPricingMode === 'BASE_500_MAIS_1_FATURAMENTO' ? 'Base mensal (BRL)' : 'Valor mensal (BRL)'}
              step={0.01}
              min={0}
              emptyFallback={0}
              value={valorParcela}
              onValueChange={onValorParcelaChange}
              disabled={isReadonly}
              highlighted={guided}
              hint={guided ? 'Informe o valor mensal do contrato.' : undefined}
            />
            </div>
            {guided ? (
              <div
                className={cn(
                  'flex flex-col gap-2 rounded-lg border-2 p-2',
                  setupChoice === 'unset' ? 'border-warning' : 'border-white/[0.08]',
                )}
              >
                <p className="text-[10px] font-medium uppercase tracking-wider text-text-label">
                  Vai ter custo de implantação?
                </p>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    disabled={isReadonly}
                    onClick={() => {
                      setSetupChoice('no')
                      onValorEntradaChange(0)
                    }}
                    className={cn(
                      'rounded-md border px-2 py-1.5 text-xs font-semibold',
                      setupChoice === 'no'
                        ? 'border-accent bg-accent text-white'
                        : 'border-white/[0.08] text-text-secondary',
                    )}
                  >
                    Não
                  </button>
                  <button
                    type="button"
                    disabled={isReadonly}
                    onClick={() => setSetupChoice('yes')}
                    className={cn(
                      'rounded-md border px-2 py-1.5 text-xs font-semibold',
                      setupChoice === 'yes'
                        ? 'border-accent bg-accent text-white'
                        : 'border-white/[0.08] text-text-secondary',
                    )}
                  >
                    Sim
                  </button>
                </div>
                {setupChoice === 'no' && (
                  <p className="text-[11px] text-text-muted">Implantação fica em R$ 0,00.</p>
                )}
                {setupChoice === 'yes' && (
                  <NumericInput
                    label="Implantação (BRL)"
                    step={0.01}
                    min={0}
                    emptyFallback={0}
                    value={valorEntrada}
                    onValueChange={onValorEntradaChange}
                    disabled={isReadonly}
                    highlighted
                  />
                )}
              </div>
            ) : (
            <NumericInput
              label="Implantação (BRL)"
              step={0.01}
              min={0}
              emptyFallback={0}
              value={valorEntrada}
              onValueChange={onValorEntradaChange}
              disabled={isReadonly}
              hint="Implantação opcional"
            />
            )}
            <NumericInput
              label="Fidelidade (meses)"
              min={1}
              emptyFallback={12}
              value={guided ? 12 : duration}
              onValueChange={onDurationChange}
              disabled={isReadonly || guided}
              hint={guided ? 'Pré-definido em 12 meses.' : undefined}
            />
            {!guided && <PlanEditorCalculatedField label="TCV Anual Total" value={brl(valorTotal)} />}
            </div>
            {guided && <PlanEditorCalculatedField label="TCV Anual Total" value={brl(valorTotal)} />}
        </div>
      </div>

      <div className="glass-card p-4 space-y-3">
        <div className="flex items-center justify-between border-b border-white/[0.06] pb-2">
          <h4 className="text-xs font-bold text-accent uppercase tracking-wider flex items-center gap-1.5">
            <Award size={14} />
            Destaque Visual & Bônus (PPT)
          </h4>
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={showBonus}
              onChange={(e) => onShowBonusChange(e.target.checked)}
              disabled={isReadonly}
              className="sr-only peer"
            />
            <div className="w-9 h-5 bg-white/[0.08] border border-white/[0.1] rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:left-[2px] after:bg-text-muted after:border-white/[0.06] after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-accent peer-checked:after:bg-white" />
            <span className="ml-2 text-xs font-medium text-text-primary">Ativar Destaque Visual de Bônus</span>
          </label>
        </div>
        {showBonus && (
          <Input
            label="Texto do Bônus / Selo de Destaque"
            value={bonusText}
            onChange={(e) => onBonusTextChange(e.target.value)}
            disabled={isReadonly}
            placeholder="Ex: Consultoria de UX Grátis · Setup Isento · Mentorias Individuais..."
          />
        )}
      </div>

      <div className="flex items-center gap-3 p-3 rounded-lg bg-[var(--brand-primary-muted)] border border-[var(--brand-primary)]/30 text-xs">
        <Layers className="text-[var(--brand-primary)] shrink-0" size={16} />
        <div className="flex-1 text-[var(--font-color-muted)]">
          Este plano possui{' '}
          <strong className="text-[var(--text-primary)]">{includedCount} entregáveis</strong> selecionados.
          Você pode ligar/desligar os itens na aba <strong>&quot;Escopo de Entregáveis&quot;</strong>.
        </div>
      </div>
    </div>
  )
}
